import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Sign in: email + password, then — if two-step login is on — the 6-digit code
// from your authenticator app (or one of your backup codes).

const ArrowIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </svg>
);

function Login() {
  const [step, setStep] = useState('password');     // password | code
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState('');
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [useBackup, setUseBackup] = useState(false);
  const [backupCode, setBackupCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const boxRefs = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    const token = window.localStorage.getItem('token');
    if (token && token !== 'null' && token !== 'undefined') {
      navigate('/companions');
    }
  }, []);

  useEffect(() => {
    if (step === 'code' && !useBackup) setTimeout(() => boxRefs.current[0]?.focus(), 0);
  }, [step, useBackup]);

  const explain = (err, fallback) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 429) return typeof detail === 'string' ? detail : 'too many tries — wait 15 minutes';
    if (typeof detail === 'string' && status !== 500) return detail === 'invalid credentials' ? fallback : detail;
    if (!err?.response) return "couldn't reach the server — check your connection";
    return fallback;
  };

  const finish = (data) => {
    window.localStorage.setItem('token', data.token);
    window.localStorage.setItem('is_admin', String(!!data.is_admin));
    navigate('/companions');
  };

  const handlePassword = async (e) => {
    e?.preventDefault();
    if (!email.trim() || !password || busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/login`, { email: email.trim(), password });
      if (res.data.two_factor_required) {
        setChallenge(res.data.challenge);
        setDigits(['', '', '', '', '', '']);
        setBackupCode('');
        setUseBackup(false);
        setStep('code');
      } else {
        finish(res.data);
      }
    } catch (err) {
      setError(explain(err, 'wrong email or password'));
    }
    setBusy(false);
  };

  const submitCode = async (code) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await axios.post(`${API}/auth/login/2fa`, { challenge, code });
      finish(res.data);
    } catch (err) {
      const msg = explain(err, "that code didn't work");
      setError(msg);
      if (/timed out/.test(msg)) {
        // the 5-minute window passed — start again from the password
        setStep('password');
        setPassword('');
      } else if (!useBackup) {
        setDigits(['', '', '', '', '', '']);
        setTimeout(() => boxRefs.current[0]?.focus(), 0);
      }
    }
    setBusy(false);
  };

  const handleCode = (e) => {
    e?.preventDefault();
    if (useBackup) {
      if (backupCode.trim()) submitCode(backupCode.trim());
    } else {
      const code = digits.join('');
      if (code.length === 6) submitCode(code);
    }
  };

  const setDigit = (i, value) => {
    const clean = value.replace(/\D/g, '');
    if (clean.length > 1) {
      // pasted or autofilled the whole code
      const all = clean.slice(0, 6).split('');
      const next = ['', '', '', '', '', ''].map((_, k) => all[k] || '');
      setDigits(next);
      boxRefs.current[Math.min(all.length, 5)]?.focus();
      if (all.length === 6) submitCode(all.join(''));
      return;
    }
    const next = [...digits];
    next[i] = clean;
    setDigits(next);
    if (clean && i < 5) boxRefs.current[i + 1]?.focus();
    if (clean && i === 5 && next.every(Boolean)) submitCode(next.join(''));
  };

  const onDigitKey = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) boxRefs.current[i - 1]?.focus();
  };

  const backToPassword = () => {
    setStep('password');
    setPassword('');
    setError('');
  };

  return (
    <div style={styles.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@1,300&family=Instrument+Sans:wght@400;500&display=swap" rel="stylesheet" />
      <style>{`
        .lg-field input:focus, .lg-box:focus { border-bottom-color: #c9935a !important; }
        .lg-go:hover, .lg-go:focus-visible { background: rgba(201,147,90,0.14) !important; border-color: #c9935a !important; outline: none; }
        .lg-link:hover { color: #e9e0d4 !important; }
        .lg-field input::placeholder, .lg-backup::placeholder { color: #4a4038; }
        input:-webkit-autofill { -webkit-text-fill-color: #e9e0d4; -webkit-box-shadow: 0 0 0 1000px #0c0a0d inset; caret-color: #e9e0d4; }
        @media (prefers-reduced-motion: reduce) { .lg-go, .lg-field input { transition: none !important; } }
      `}</style>

      <main style={styles.stage}>
        <div style={styles.col}>
          <div style={styles.mark}>re<span style={styles.markV}>v</span>erie</div>

          {step === 'password' ? (
            <form style={styles.form} onSubmit={handlePassword}>
              <div className="lg-field" style={styles.field}>
                <label htmlFor="lg-email" style={styles.label}>email</label>
                <input
                  id="lg-email" type="email" autoComplete="username" autoFocus
                  style={styles.input} value={email} onChange={e => setEmail(e.target.value)}
                />
              </div>
              <div className="lg-field" style={styles.field}>
                <label htmlFor="lg-password" style={styles.label}>password</label>
                <input
                  id="lg-password" type="password" autoComplete="current-password"
                  style={styles.input} value={password} onChange={e => setPassword(e.target.value)}
                />
              </div>
              <div style={styles.row}>
                <div style={styles.msg} aria-live="polite">{error}</div>
                <button className="lg-go" type="submit" style={{ ...styles.go, opacity: busy ? 0.5 : 1 }} disabled={busy} aria-label="sign in">
                  <ArrowIcon />
                </button>
              </div>
            </form>
          ) : (
            <form style={styles.form} onSubmit={handleCode}>
              <p style={styles.intro}>
                {useBackup
                  ? 'enter one of your backup codes'
                  : 'enter the 6-digit code from your authenticator app'}
              </p>

              {useBackup ? (
                <div className="lg-field" style={styles.field}>
                  <input
                    className="lg-backup" autoFocus autoComplete="off" spellCheck={false}
                    placeholder="xxxx-xxxx-xxxx" aria-label="backup code"
                    style={{ ...styles.input, ...styles.backupInput }}
                    value={backupCode} onChange={e => setBackupCode(e.target.value)}
                  />
                </div>
              ) : (
                <div style={styles.boxes}>
                  {digits.map((d, i) => (
                    <input
                      key={i}
                      ref={el => { boxRefs.current[i] = el; }}
                      className="lg-box"
                      style={styles.box}
                      inputMode="numeric"
                      autoComplete={i === 0 ? 'one-time-code' : 'off'}
                      aria-label={`digit ${i + 1}`}
                      value={d}
                      onChange={e => setDigit(i, e.target.value)}
                      onKeyDown={e => onDigitKey(i, e)}
                    />
                  ))}
                </div>
              )}

              <div style={styles.row}>
                <div style={styles.links}>
                  <button type="button" className="lg-link" style={styles.link}
                    onClick={() => { setUseBackup(!useBackup); setError(''); }}>
                    {useBackup ? 'use the app code' : 'use a backup code'}
                  </button>
                  <button type="button" className="lg-link" style={styles.link} onClick={backToPassword}>
                    start over
                  </button>
                </div>
                <button className="lg-go" type="submit" style={{ ...styles.go, opacity: busy ? 0.5 : 1 }} disabled={busy} aria-label="verify">
                  <ArrowIcon />
                </button>
              </div>
              <div style={styles.msg} aria-live="polite">{error}</div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh', background: '#0c0a0d', color: '#e9e0d4',
    backgroundImage: 'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(201,147,90,0.08), transparent 70%)',
    fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: '15px',
    WebkitFontSmoothing: 'antialiased', padding: '0 16px', boxSizing: 'border-box',
  },
  stage: { minHeight: '100vh', display: 'grid', placeItems: 'center', padding: '48px 0 96px', boxSizing: 'border-box' },
  col: { width: '100%', maxWidth: '320px', display: 'grid', gap: '40px' },
  mark: {
    fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: 'italic', fontWeight: 300,
    fontSize: '30px', letterSpacing: '0.02em', textAlign: 'center',
  },
  markV: { color: '#c9935a' },
  form: { display: 'grid', gap: '22px' },
  field: { display: 'grid', gap: '6px' },
  label: { fontSize: '11px', letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6f6357' },
  input: {
    background: 'transparent', border: 0, borderBottom: '1px solid #2a2329', borderRadius: 0,
    padding: '8px 0 10px', color: '#e9e0d4', font: 'inherit', fontSize: '16px', outline: 'none',
    transition: 'border-color .2s', width: '100%', boxSizing: 'border-box',
  },
  backupInput: { textAlign: 'center', letterSpacing: '0.12em', fontVariantNumeric: 'tabular-nums' },
  row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginTop: '6px' },
  msg: { fontSize: '13px', color: '#e0a08f', minHeight: '18px', lineHeight: 1.5 },
  go: {
    width: '44px', height: '44px', borderRadius: '50%', border: '1px solid rgba(201,147,90,0.14)',
    background: 'transparent', color: '#c9935a', display: 'grid', placeItems: 'center',
    cursor: 'pointer', flexShrink: 0, transition: 'background .2s, border-color .2s', padding: 0,
  },
  intro: { fontSize: '13px', color: '#6f6357', lineHeight: 1.6, textAlign: 'center', margin: '-18px 0 0' },
  boxes: { display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '8px' },
  box: {
    width: '100%', aspectRatio: '4 / 5', textAlign: 'center', fontFamily: "'Instrument Sans', system-ui, sans-serif",
    fontWeight: 500, fontSize: '20px', color: '#e9e0d4', background: 'transparent', border: 0,
    borderBottom: '1px solid #2a2329', outline: 'none', borderRadius: 0,
    fontVariantNumeric: 'tabular-nums', boxSizing: 'border-box', padding: 0,
  },
  links: { display: 'flex', gap: '14px', flexWrap: 'wrap' },
  link: {
    background: 'none', border: 0, padding: 0, color: '#6f6357', font: 'inherit', fontSize: '13px',
    cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px', textDecorationColor: '#2a2329',
  },
};

export default Login;
